import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { getAnalyticsInstance, isAnalyticsInitialized } from '../config/firebase';

class AnalyticsService {
  constructor() {
    this.analytics = null;
    this.pendingEvents = []; // Черга подій, які чекають ініціалізації
    this.checkAnalytics();
  }

  // Перевіряємо доступність Analytics
  checkAnalytics() {
    // Перевіряємо кожні 100мс, поки analytics не ініціалізується
    const checkInterval = setInterval(() => {
      if (isAnalyticsInitialized()) {
        this.analytics = getAnalyticsInstance();
        
        // Обробляємо всі події, які чекали ініціалізації
        this.processPendingEvents();
        
        clearInterval(checkInterval);
      }
    }, 100);

    // Зупиняємо перевірку через 10 секунд
    setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);
  }

  // Обробляємо події, які чекали ініціалізації
  processPendingEvents() {
    this.pendingEvents.forEach(({ eventName, parameters }) => {
      this.logEventInternal(eventName, parameters);
    });
    this.pendingEvents = [];
  }

  // Перевіряємо, чи доступний Analytics
  isAvailable() {
    return this.analytics !== null;
  }

  // Логування подій
  logEvent(eventName, parameters = {}) {
    if (!this.isAvailable()) {
      this.pendingEvents.push({ eventName, parameters });
      return;
    }

    this.logEventInternal(eventName, parameters);
  }

  // Внутрішній метод для логування подій
  logEventInternal(eventName, parameters = {}) {
    try {
      logEvent(this.analytics, eventName, parameters);
    } catch (error) {
      console.error('[Analytics] Error logging event:', error);
    }
  }

  // Встановлення ID користувача
  setUserId(userId) {
    if (!this.isAvailable()) {
      return;
    }

    try {
      setUserId(this.analytics, userId);
    } catch (error) {
      console.error('Error setting analytics user ID:', error);
    }
  }

  // Встановлення властивостей користувача
  setUserProperties(properties) {
    if (!this.isAvailable()) {
      return;
    }

    try {
      setUserProperties(this.analytics, properties);
    } catch (error) {
      console.error('Error setting analytics user properties:', error);
    }
  }

  // Події авторизації
  logLogin(method = 'email') {
    this.logEvent('web_login', { method });
  }

  logSignUp(method = 'email') {
    this.logEvent('web_sign_up', { method });
  }

  logLogout() {
    this.logEvent('web_logout');
  }

  // Події чату
  logChatMessage(messageLength = 0) {
    this.logEvent('web_chat_message_sent', { 
      message_length: messageLength 
    });
  }

  logChatStarted() {
    this.logEvent('web_chat_started');
  }

  logChatEnded() {
    this.logEvent('web_chat_ended');
  }

  // Події навігації
  logPageView(pageName) {
    this.logEvent('web_page_view', { page_name: pageName });
  }

  logScreenView(screenName) {
    this.logEvent('web_screen_view', { screen_name: screenName });
  }

  // Події помилок
  logError(errorType, errorMessage) {
    this.logEvent('web_error_occurred', {
      error_type: errorType,
      error_message: errorMessage
    });
  }

  // Події продуктивності
  logPerformance(metricName, value, unit = 'ms') {
    this.logEvent('web_performance_metric', {
      metric_name: metricName,
      value: value,
      unit: unit
    });
  }

  // Події функціональності
  logFeatureUsed(featureName) {
    this.logEvent('web_feature_used', { feature_name: featureName });
  }

  logButtonClick(buttonName, location) {
    this.logEvent('web_button_click', { 
      button_name: buttonName,
      location: location 
    });
  }
}

// Створюємо єдиний екземпляр сервісу
const analyticsService = new AnalyticsService();

export default analyticsService;
